import * as Icons from '@radix-ui/react-icons'
import { Skeleton, Spinner } from '@radix-ui/themes'
import Avatar from 'components/Avatar/Avatar'
import { useAuth } from 'hooks/useAuth'
import {
  Check,
  ChevronsUpDown,
  Search,
} from 'lucide-react'
import { useMemo, useState, useRef, useEffect } from 'react'
import { useFilter } from 'react-aria'
import {
  Button,
  Input,
  ListBox,
  ListBoxItem,
  Menu,
  MenuItem,
  MenuTrigger,
  Popover,
  SearchField,
} from 'react-aria-components'
import { useNavigate } from 'react-router-dom'
import ImpersonateModal from './ImpersonateModal'

export default function AccountSelector({
  open,
  onClick = null,
  onClose = null,
}) {
  const { orgs, selectOrg, user, signOut, credits, org, loading, restoreAdminUser, isImpersonating } = useAuth()
  const navigate = useNavigate()
  const [openImpersonateModal, setOpenImpersonateModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')  
  const searchInputRef = useRef(null)

  const canImpersonate = useMemo(() => {
    return user?.user?.account_type === 'admin'
  }, [user])

  // Use the useFilter hook for better filtering
  const { contains } = useFilter({ sensitivity: 'base' })

  const filteredOrgs = useMemo(() => {
    if (!orgs)
      return []
    if (!searchQuery)
      return orgs

    return orgs.filter(org =>
      contains(org.org_display_name || 'Unknown Org', searchQuery),
    )
  }, [orgs, searchQuery, contains])

  // Focus the search input when the popover opens
  useEffect(() => {
    if (open && searchInputRef.current) {
      // Small delay to ensure popover is fully rendered
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 0)
    }
  }, [open])

  return (
    <div className="w-full">
      <MenuTrigger
        isOpen={open}
        onOpenChange={(isOpen) => {
          if (onClose && !isOpen) {
            onClose()
          }
          if (onClick && isOpen) {
            onClick()            
          }
        }}
      >
        <Button
          className={`
            flex w-full cursor-pointer flex-row flex-nowrap items-center
            justify-between gap-[5px] rounded-[10px] border-none p-0 text-left
            ${
    open ? 'bg-[#f5f5f5]' : 'bg-transparent'
    }
          `}
        >
          {!user || loading === true
            ? (
                <>
                  <Skeleton style={{ height: 24, width: 240 }} />
                </>
              )
            : (
                <>
                  <Avatar org={org} />
                  <div
                    className={`
                      ml-[10px] w-[600px] overflow-hidden text-ellipsis
                      whitespace-nowrap
                    `}
                  >
                    <p className="text-[10px] text-[GrayText]">
                      {loading
                        ? (
                            <Spinner
                              size="1"
                              style={{ height: 10, marginTop: 5 }}
                            />
                          )
                        : (
                            `${credits?.toFixed(2) || 0} credits`
                          )}
                      {' '}
                    </p>
                  </div>
                  <ChevronsUpDown
                    size={16}
                    color="#000"
                    className="ml-auto"
                  />
                </>
              )}
        </Button>
        <Popover
          placement="top start"
          className={`
            h-fit min-w-[300px] overflow-hidden rounded-md border border-dark-secondary
            bg-dark-primary
            shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.1)]
          `}
        >
          <div className="flex flex-col">
            {/* Search Section */}
            <div
              className="border-b border-dark-secondary p-2"
            >
              <SearchField
                value={searchQuery}
                onChange={setSearchQuery}
                aria-label="Search organizations"
              >
                <div
                  className="relative flex h-10 items-center"
                >
                  <Search
                    className="absolute top-1/2 left-[10px] -translate-y-1/2 text-foreground-inverse-secondary"
                    size={13}
                  />
                  <Input
                    ref={searchInputRef}
                    className={`
                      h-10 w-full border-none bg-transparent px-2 py-2 pl-8
                      text-[13px] font-normal
                      text-foreground-inverse outline-none
                      placeholder:text-foreground-inverse-secondary
                    `}
                    placeholder="Find an org..."
                  />
                </div>
              </SearchField>
            </div>

            {/* Organizations List with Scroll */}
            {filteredOrgs.length > 0 && (
              <div
                className={`
                  max-h-[240px] overflow-y-auto border-b
                  border-dark-secondary
                `}
              >
                <ListBox
                  className="p-2 outline-none"
                  key="organizations-listbox"
                  items={filteredOrgs}
                  selectionMode="single"
                  selectedKeys={org ? [`org-${org.id}`] : []}
                  onSelectionChange={(keys) => {
                    const selectedId = Array.from(keys)[0]
                    if (selectedId) {
                      const orgId = selectedId.replace('org-', '')
                      selectOrg(orgId)
                    }
                  }}
                  aria-label="Organizations"
                >
                  {item => (
                    <ListBoxItem
                      key={`org-${item.org_id}`}
                      id={`org-${item.org_id}`}
                      className={`
                        flex w-full cursor-pointer flex-row items-center
                        rounded-sm border-none p-[6px]
                        transition-colors outline-none
                        hover:bg-hover-dark-16
                        selected:bg-dark-tertiary
                      `}
                    >
                      <Avatar org={item} size={28} />
                      <div className="ml-[15px]">
                        <p
                          className={`
                            text-[14px] font-[450]
                            text-foreground-inverse
                          `}
                        >
                          {item.org_display_name?.length > 20
                            ? `${item.org_display_name?.substring(0, 20)}...`
                            : item.org_display_name}
                        </p>
                        <p
                          className={`
                            -mt-[3px] text-[11px] font-normal
                            text-foreground-inverse-secondary
                          `}
                        >
                          {item.permissions?.[0]}
                        </p>
                      </div>
                      {org?.id === item.org_id && (
                        <Check
                          className="ml-auto text-foreground-inverse"
                          size={13}
                        />
                      )}
                    </ListBoxItem>
                  )}
                </ListBox>
              </div>
            )}

            {/* Action Items */}
            <Menu
              className="p-2 outline-none"
              items={[
                ...(canImpersonate ? [{ id: 'impersonate', type: 'action', label: 'Impersonate', icon: Icons.IdCardIcon }] : []),
                ...(isImpersonating ? [{ id: 'restore-admin', type: 'action', label: 'Restore Admin', icon: Icons.ExitIcon }] : []),
                { id: 'settings', type: 'action', label: 'Settings', icon: Icons.GearIcon },
                { id: 'discord', type: 'action', label: 'Discord', icon: Icons.DiscordLogoIcon },
                { id: 'docs', type: 'action', label: 'Documentation', icon: Icons.ReaderIcon },
                { id: 'university', type: 'action', label: 'Bland University', icon: Icons.CardStackMinusIcon },
                { id: 'signout', type: 'action', label: 'Sign out', icon: Icons.ExitIcon, isSignOut: true },
              ]}
            >
              {(item) => {
                const Icon = item.icon
                return (
                  <MenuItem
                    key={item.id}
                    onAction={() => {
                      switch (item.id) {
                        case 'impersonate':
                          setOpenImpersonateModal(true)
                          break
                        case 'restore-admin':
                          restoreAdminUser()
                          break
                        case 'settings':
                          navigate('/dashboard/settings')
                          break
                        case 'discord':
                          window.open('https://discord.com/invite/QvxDz8zcKe', '_blank')
                          break
                        case 'docs':
                          window.open('https://docs.bland.ai', '_blank')
                          break
                        case 'university':
                          window.open('https://university.bland.ai', '_blank')
                          break
                        case 'signout':
                          signOut()
                          break
                      }
                    }}
                    className={`
                      flex w-full cursor-pointer flex-row items-center
                      justify-start gap-[10px] rounded-sm
                      border-none bg-transparent p-2 outline-none
                      hover:bg-hover-dark-16
                    `}
                  >
                    <Icon className="text-foreground-inverse-secondary" />
                    <p
                      className={`
                        text-[14px] font-[450]
                        ${item.isSignOut
                    ? `text-foreground-inverse`
                    : `text-foreground-inverse-secondary`}
                      `}
                    >
                      {item.label}
                    </p>
                  </MenuItem>
                )
              }}
            </Menu>
          </div>
        </Popover>
      </MenuTrigger>

      <ImpersonateModal
        isOpen={openImpersonateModal}
        onOpenChange={setOpenImpersonateModal}
      />
    </div>
  )
}
